import { TreeSway } from "./tree_sway"
import { TreeHit } from "./tree_hit"
import { TreeDamage } from "./tree_damage"
import { TreeFall, fallSide } from "./tree_fall"
import { WindLeaves } from "./wind_leaves"
import { FootDust } from "./foot_dust"
import { getRendererApp } from "../renderer_app"
import { isTreeEntityTile } from "game-common/map/map"
import { contentTexture } from "../content_textures"
import { Container, Sprite, Texture, Graphics } from "pixi.js"
import { Coordinate, WindLeavesConfig, FragmentSpriteMeta, TileSize, randomIntBetween } from "game-common/models"
import { MapDelta, TileEffect } from "game-common/map/map"
import { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import { FragmentEffect, FragmentEffectOptions } from "./fragment_effect"
import { HoleEffect } from "./hole_effect"
import { WorldOverlaySurface } from "../world_overlay_surface"
import { roughToFineCoordinates, between } from "game-common/util"
import { BitmapText } from "pixi.js"
import { ScalingParams } from "../../../../client_util"
import { showTileRepairEffect } from "./tile_repair_effect"
import { AnimatedFeedback } from "../effect/animated_feedback"

// SurfaceObject interface moved here from tile_damage_manager.ts
export interface SurfaceObject {
    spriteSet: Sprite[]
    compositeTileId?: string
    tilesetId?: string
    tilesetTileId?: string
    flags?: Set<string>
    damageStep?: number
    delta?: MapDelta
    isShaking?: boolean
    treeWind?: { x: number; y: number }
}

/**
 * Configuration options for the TileEffectSystem
 */
export interface TileEffectSystemConfig {
    surface: Container
    worldOverlaySurface: WorldOverlaySurface
    tilemapMetaProvider: TileMapMetaProvider
    holeEffect: HoleEffect
    skySpriteSurfaceAbove: Container
    skySpriteSurfaceBelow: Container
    attachTexture: (texture: Texture, surface?: Container) => Sprite
    renderCompositeTile: (
        tilemapMetaProvider: TileMapMetaProvider,
        parentTileId: string,
        compositeTileId: string,
        x: number,
        y: number,
        zIndex?: number,
    ) => void
    handleRenderCompositeTile: (
        key: string,
        tilemapMetaProvider: TileMapMetaProvider,
        compositeTileId: string,
        tilesetId: string,
    ) => Sprite[]
}

/**
 * Interface for the unified tile effect system
 * This system handles all tile-related visual effects including:
 * - Sprite fragment rendering
 * - Tile effects (damage, destroy, remove)
 * - Damage visualization
 * - Fragment effects
 */
export interface ITileEffectSystem {
    // Sprite fragment rendering
    renderSpriteFragment(fragment: FragmentSpriteMeta): void
    processSurfaceId(tilesetTileId: string, surfaceId: string, sprite: Sprite, delta?: MapDelta): void
    detachSpritesFromSurface(surfaceId: string, sprites: Sprite[]): void
    snapshotSurfaceForDestroy(surfaceId: string): void

    // Tile effects
    applyTileEffect(effect: TileEffect): void
    applyDamageEffect(effect: TileEffect, surfaceObject: SurfaceObject): void
    applyDestroyEffect(effect: TileEffect, surfaceObject: SurfaceObject): void
    applyRemoveEffect(effect: TileEffect): void

    // Fragment effects
    createFragmentEffect(textures: Array<{ texture: Texture; x: number; y: number }>, groundLevel: number): void
    createSingleFragmentEffect(texture: Texture, parentX: number, parentY: number): void

    // Damage visualization
    applyDamageVisualizationToExistingTiles(surfaceObjectsByKey: Map<string, SurfaceObject>): void

    // Configuration
    updateTilemapMetaProvider(tilemapMetaProvider: TileMapMetaProvider): void
    updateSkySurfaces(skySpriteSurfaceAbove: Container, skySpriteSurfaceBelow: Container): void
    setSurfaces(surface: Container, worldOverlaySurface: WorldOverlaySurface): void
    getHoleEffect(): HoleEffect

    // Convenience methods
    createDamageOverlaySprite(tilesetTileId: string, x: number, y: number, surface?: Container): Sprite
    shouldCreateDamageOverlay(delta?: MapDelta): boolean
    updateHoleEffectContext(roomId: string, rng: () => number): void
    clearSurfaceObjects(): void
    update(wind?: WindLeavesConfig | null, center?: Coordinate, storm?: number, brightness?: number): void
    predictTreeStrike(effect: TileEffect): void
}

/**
 * Unified system for handling all tile-related visual effects
 * Consolidates functionality from MapRenderingManager and TileDamageManager
 */
export class TileEffectSystem implements ITileEffectSystem {
    private surfaceObjectsByKey: Map<string, SurfaceObject>
    private config: TileEffectSystemConfig
    private treeSway: TreeSway
    private treeHit: TreeHit
    private treeFall: TreeFall
    private treeDamage = new TreeDamage()
    // Where each tree was last struck from, so its fall can go away from whoever felled it even when the
    // felling names no striker. Entries go when the tree falls or the room changes.
    private lastStrikeFrom = new Map<string, { x: number; y: number }>()
    private predictedHits = new Map<string, Array<{ strikerId: string; at: number }>>()
    // Last damage for live tiles in this room. Baked-in ore has no HP delta, so retain its
    // visual state across map redraws. Entries are removed on destruction/removal or room change.
    private damagedTiles = new Map<string, TileEffect>()
    // Shatter textures captured when a wall's cell was removed, keyed by surfaceId. Lets a "destroy"
    // tile-effect that arrives AFTER the removal-triggered rerender (which cleared the live surface)
    // still play the shatter. Bounded; entries are consumed on use.
    private removedSurfaceSnapshots: Map<string, { textures: Array<{ texture: Texture; x: number; y: number }>; groundLevel: number }> =
        new Map()

    constructor(config: TileEffectSystemConfig, surfaceObjectsByKey: Map<string, SurfaceObject>) {
        this.config = config
        this.surfaceObjectsByKey = surfaceObjectsByKey

        this.treeSway = new TreeSway(surfaceObjectsByKey)
        this.treeHit = new TreeHit(config.surface, () => {
            const renderer = getRendererApp().renderer
            return { leaves: WindLeaves.textureFactory(renderer)(), puffs: FootDust.textureFactory(renderer)() }
        })
        this.treeFall = new TreeFall(config.surface, this.treeHit.debris)

        // Configure hole effect with proper colors
        this.configureHoleEffect()
    }

    /**
     * Configure the hole effect with proper colors
     */
    private configureHoleEffect = (): void => {
        this.config.holeEffect.configureScuffMarks({
            enabled: true,
            count: 3,
            color: 0x000000, // Black
            alpha: 0.15, // 0.15 opacity as requested
            minSize: 0.05, // 5% of texture size
            maxSize: 0.15, // 15% of texture size
            roughness: 0.7,
        })
        this.config.holeEffect.configureDents({
            enabled: true,
            count: 2,
            color: 0x1a1a1a, // Very dark gray/black
            alpha: 0.4,
            minSize: 0.08, // 8% of texture size
            maxSize: 0.2, // 20% of texture size
            depth: 0.6,
        })
    }

    // ============================================================================
    // Sprite Fragment Rendering Methods
    // ============================================================================

    /**
     * Render a sprite fragment with all its map entries
     */
    public renderSpriteFragment = (fragment: FragmentSpriteMeta): void => {
        const { location, height, mapEntries, fineLocationNudge, debugTxt } = fragment

        if (debugTxt) {
            const debugTxtLocation = roughToFineCoordinates({ x: location.x, y: location.y + 0 })
            const debugTextObj = new BitmapText(debugTxt, {
                fontName: "SignFont",
                align: "center",
            })
            debugTextObj.x = debugTxtLocation.x
            debugTextObj.y = debugTxtLocation.y

            this.config.surface.addChild(debugTextObj)
            return
        }

        const key = `frag-${fragment.id}-${location.x}-${location.y}`
        const set = []
        this.surfaceObjectsByKey.set(key, {
            spriteSet: set,
        })

        const bottom = roughToFineCoordinates({ x: location.x, y: location.y + 0 })
        bottom.x += fineLocationNudge?.x || 0
        bottom.y += fineLocationNudge?.y || 0
        let maxX = undefined
        let maxY = undefined
        const debug = false
        const renderZIdxOffset = mapEntries[0].renderZIdxOffset || 0

        mapEntries.forEach(mapEntry => {
            const tile: string = `${mapEntry.tilePos}`
            const compositeTileId = this.config.tilemapMetaProvider.getCompositeTileId(`${mapEntry.tilePos}`)

            if (compositeTileId) {
                this.config.renderCompositeTile(
                    this.config.tilemapMetaProvider,
                    tile,
                    compositeTileId,
                    mapEntry.x,
                    mapEntry.y,
                    renderZIdxOffset + 200,
                )
            } else {
                const tilesetTileId = this.config.tilemapMetaProvider.resolveTilesetTileId(tile)
                const g = this.config.attachTexture(
                    contentTexture(`${tilesetTileId}`),
                    mapEntry.roofLayer === "below"
                        ? this.config.skySpriteSurfaceBelow
                        : mapEntry.roofLayer === "above"
                        ? this.config.skySpriteSurfaceAbove
                        : undefined,
                )
                const fine = roughToFineCoordinates({ x: mapEntry.x, y: mapEntry.y })
                fine.x += fineLocationNudge?.x || 0
                fine.y += fineLocationNudge?.y || 0
                g.x = fine.x
                g.y = fine.y
                g.zIndex = 100 + bottom.y - TileSize * 0.5
                set.push(g)

                if (debug) {
                    const f = new Graphics()
                    f.beginFill(0x00ff00)
                    f.drawCircle(0, 0, 5)
                    f.endFill()
                    f.x = fine.x
                    f.y = fine.y
                    f.zIndex = 100000
                    this.config.surface.addChild(f)
                }
                if (maxY === undefined || fine.y > maxY) {
                    maxY = fine.y
                }
                if (maxX == undefined) {
                    maxX = fine.x
                }
            }
        })

        if (maxX !== undefined && maxY !== undefined) {
            const z = maxY + TileSize * 0.35
            set.forEach(sprite => {
                sprite.zIndex = 100 + renderZIdxOffset + z
            })

            if (debug) {
                const f = new Graphics()
                f.beginFill(0xff0000)
                f.drawCircle(0, 0, 5)
                f.endFill()
                f.x = maxX
                f.y = z
                f.zIndex = 100000
                this.config.surface.addChild(f)
            }
        }
    }

    /**
     * Process surface ID for sprite grouping
     * Maintains consistent Y-coordinate ordering for proper visual layering
     */
    public processSurfaceId = (
        tilesetTileId: string,
        surfaceId: string,
        spriteToReturn: Sprite,
        delta?: MapDelta,
    ): void => {
        if (!surfaceId) {
            return
        }

        let surfaceObject: SurfaceObject = this.surfaceObjectsByKey.get(surfaceId)
        if (!surfaceObject) {
            surfaceObject = {
                spriteSet: [spriteToReturn],
            }
            this.surfaceObjectsByKey.set(surfaceId, surfaceObject)
        } else {
            // Insert sprite in correct Y-order to maintain consistent ordering
            // This ensures the same ordering used in damage visualization
            const insertIndex = surfaceObject.spriteSet.findIndex(sprite => sprite.y > spriteToReturn.y)
            if (insertIndex === -1) {
                surfaceObject.spriteSet.push(spriteToReturn)
            } else {
                surfaceObject.spriteSet.splice(insertIndex, 0, spriteToReturn)
            }
        }
        if (delta) {
            surfaceObject.delta = delta
        }
        if (tilesetTileId) {
            surfaceObject.tilesetTileId = tilesetTileId
        }
    }

    /**
     * Inverse of processSurfaceId's insertion: drop the given sprites from a surface's spriteSet by
     * identity. Called when an incremental repaint destroys a cell's owned sprites -- without this the
     * shared spriteSet keeps references to destroyed sprites, and the next damage/shake/hole pass reads
     * `.x` / `.texture` on a destroyed sprite (transform is null) and throws. If the set empties out the
     * surface object is removed entirely so a stale, spriteless entry can't linger.
     */
    public detachSpritesFromSurface = (surfaceId: string, sprites: Sprite[]): void => {
        if (!surfaceId || !sprites || sprites.length === 0) {
            return
        }
        const surfaceObject = this.surfaceObjectsByKey.get(surfaceId)
        if (!surfaceObject || !surfaceObject.spriteSet) {
            return
        }
        surfaceObject.spriteSet = surfaceObject.spriteSet.filter(s => !sprites.includes(s))
        if (surfaceObject.spriteSet.length === 0) {
            this.surfaceObjectsByKey.delete(surfaceId)
        }
    }

    /**
     * Capture a surface's current shatter textures + positions before its sprites are torn down by the
     * removal-triggered rerender. A wall's "destroy" tile-effect can arrive AFTER that rerender (the
     * removal delta beats the effect), by which point the live surface is gone; the snapshot lets the
     * shatter still play. Called per removed cell -- a multi-cell wall snapshots the same surfaceId
     * (idempotent, captures the full sprite set each time). Bounded so a missing destroy effect can't
     * grow it without limit.
     */
    public snapshotSurfaceForDestroy = (surfaceId: string): void => {
        if (!surfaceId) {
            return
        }
        const surfaceObject = this.surfaceObjectsByKey.get(surfaceId)
        const set = (surfaceObject?.spriteSet || []).filter(s => s && !s.destroyed && !!s.transform)
        if (set.length === 0) {
            return
        }
        let minY = Infinity
        let maxY = -Infinity
        set.forEach(g => {
            if (g.y < minY) minY = g.y
            if (g.y + g.height > maxY) maxY = g.y + g.height
        })
        const groundLevel = maxY - (maxY - minY) / 2
        const textures = set.map(g => ({ texture: g.texture, x: g.x, y: g.y }))
        this.removedSurfaceSnapshots.set(surfaceId, { textures, groundLevel })
        if (this.removedSurfaceSnapshots.size > 32) {
            const oldest = this.removedSurfaceSnapshots.keys().next().value
            this.removedSurfaceSnapshots.delete(oldest)
        }
    }

    // Play the shatter from a snapshot captured at removal time; consume it so it can't be reused.
    private shatterFromSnapshot = (surfaceId: string): void => {
        const snap = this.removedSurfaceSnapshots.get(surfaceId)
        if (!snap) {
            return
        }
        this.removedSurfaceSnapshots.delete(surfaceId)
        // A destroyed PIXI texture nulls its baseTexture/orig; skip those so the fragment effect never
        // reads a torn-down texture.
        const textures = snap.textures.filter(t => t.texture && (t.texture as any).baseTexture && (t.texture as any).orig)
        if (textures.length === 0) {
            return
        }
        this.createFragmentEffect(textures, snap.groundLevel)
    }

    // ============================================================================
    // Tile Effect Methods
    // ============================================================================

    /**
     * Apply a tile effect (damage, destroy, remove)
     */
    public applyTileEffect = (effect: TileEffect): void => {
        if (effect.effectType === "repair") {
            const { x, y } = effect.mapRoughCoordinates
            const location = this.config.worldOverlaySurface.computeLocation((x + 0.5) * TileSize, y * TileSize)
            const key = effect.surfaceId || `${effect.tileId}-${x}-${y}`
            const surfaceObject = this.surfaceObjectsByKey.get(key)
            if (effect.hp !== undefined && effect.hpMax !== undefined) {
                this.damagedTiles.set(key, { ...effect, effectType: "damage" })
                if (surfaceObject) this.applyDamageVisualization(effect, surfaceObject, surfaceObject.spriteSet, x, y, false)
            }
            showTileRepairEffect(this.config.worldOverlaySurface, location).scale.set(ScalingParams.surfaceZoom)
            return
        }
        if (!effect.tileId && !effect.surfaceId) {
            // we need either the tileId or surfaceId to do an effect
            return
        }
        if (effect.effectType === "remove" || effect.effectType === "destroy") {
            const { x, y } = effect.mapRoughCoordinates
            this.damagedTiles.delete(effect.surfaceId || `${effect.tileId}-${x}-${y}`)
        }

        if (effect.effectType === "remove") {
            this.applyRemoveEffect(effect)
        } else if (effect.effectType === "destroy") {
            const { tileId, surfaceId, mapRoughCoordinates } = effect
            const { x, y } = mapRoughCoordinates
            const key = surfaceId || `${tileId}-${x}-${y}`
            const surfaceObject = this.surfaceObjectsByKey.get(key)
            if (surfaceObject) {
                this.applyDestroyEffect(effect, surfaceObject)
            } else {
                // The live surface was already cleared by a removal-triggered rerender that beat this
                // destroy effect -- play the shatter from the snapshot captured at removal time.
                this.shatterFromSnapshot(key)
            }
        } else if (effect.effectType === "damage") {
            const { tileId, surfaceId, mapRoughCoordinates } = effect
            const { x, y } = mapRoughCoordinates
            const key = surfaceId || `${tileId}-${x}-${y}`
            const surfaceObject = this.surfaceObjectsByKey.get(key)
            if (surfaceObject) {
                this.applyDamageEffect(effect, surfaceObject)
            }
        }
    }

    /**
     * Apply damage effect to a tile
     */
    public applyDamageEffect = (effect: TileEffect, surfaceObject: SurfaceObject): void => {
        if (effect.effectType !== "damage") {
            return
        }

        const { tileId, surfaceId, mapRoughCoordinates } = effect
        const { x, y } = mapRoughCoordinates
        const key = surfaceId || `${tileId}-${x}-${y}`
        this.damagedTiles.set(key, effect)
        // Defensive: an incremental repaint can leave destroyed sprites in the shared spriteSet
        // (removeSpritedCell detaches them via detachSpritesFromSurface, but guard here too). A
        // destroyed sprite's transform is null, so reading `.x` in the shake or `.texture` in the hole
        // pass throws "Cannot read properties of null (reading 'position')". Drop them in place so both
        // the shake (set) and the hole pass (surfaceObject.spriteSet) only ever touch live sprites.
        if (surfaceObject?.spriteSet) {
            surfaceObject.spriteSet = surfaceObject.spriteSet.filter(s => s && !s.destroyed && s.transform)
        }
        const set = surfaceObject?.spriteSet

        if (!set || set.length === 0) {
            return
        }

        if (this.isTree(surfaceObject, effect)) {
            if (effect.strikeFrom) this.lastStrikeFrom.set(key, effect.strikeFrom)
            const pending = (this.predictedHits.get(key) || []).filter(hit => performance.now() - hit.at < 2000)
            const ownHit = pending.findIndex(hit => hit.strikerId === effect.strikerId)
            if (ownHit >= 0) pending.splice(ownHit, 1)
            else this.strikeTree(key, surfaceObject, effect)
            if (pending.length) this.predictedHits.set(key, pending)
            else this.predictedHits.delete(key)
            this.applyDamageVisualization(effect, surfaceObject, set, x, y, false)
            return
        }

        // Prevent multiple shake animations on the same tiles
        if (surfaceObject.isShaking) {
            return
        }

        surfaceObject.isShaking = true
        this.performShakeAnimation(set, surfaceObject, () => {
            // A redraw can recycle these pooled sprites during the shake. Only touch the
            // still-current surface, and use the latest hit if another arrived mid-animation.
            const latest = this.damagedTiles.get(key)
            if (latest && this.surfaceObjectsByKey.get(key) === surfaceObject && set.some(s => s.parent)) {
                this.applyDamageVisualization(latest, surfaceObject, set, x, y)
            }
        }, () => this.damagedTiles.has(key) && this.surfaceObjectsByKey.get(key) === surfaceObject)
    }

    /**
     * Apply destroy effect to a tile
     */
    public applyDestroyEffect = (effect: TileEffect, surfaceObject: SurfaceObject): void => {
        const { tileId, surfaceId, mapRoughCoordinates } = effect
        const { x, y } = mapRoughCoordinates
        // Defensive: drop any destroyed sprites left in the shared spriteSet by an incremental repaint
        // before we read their geometry (g.y + g.height below throws on a destroyed sprite). See the
        // matching note in applyDamageEffect.
        if (surfaceObject?.spriteSet) {
            surfaceObject.spriteSet = surfaceObject.spriteSet.filter(s => s && !s.destroyed && s.transform)
        }
        const set = surfaceObject?.spriteSet

        if (set && set.length > 0 && this.isTree(surfaceObject, effect)) {
            this.fellTree(surfaceId || `${tileId}-${x}-${y}`, set, effect)
            set.forEach(g => g.parent?.removeChild(g))
            return
        }

        if (set && set.length > 0) {
            // Calculate the ground level (bottom of the lowest sprite)
            let groundLevel = 0
            let minX = Infinity
            let maxX = -Infinity
            let minY = Infinity
            let maxY = -Infinity

            set.forEach(g => {
                const spriteBottomY = g.y + g.height
                if (spriteBottomY > groundLevel) {
                    groundLevel = spriteBottomY
                }
                if (g.x < minX) minX = g.x
                if (g.x + g.width > maxX) maxX = g.x + g.width
                if (g.y < minY) minY = g.y
                if (g.y + g.height > maxY) maxY = g.y + g.height
            })

            // Ground level should be one tile height above the bottom of the lowest sprite
            const totalHeight = maxY - minY
            groundLevel = maxY - totalHeight / 2

            // Prepare texture data for multi-texture effect
            const textures = Array.from(set).map(g => ({
                texture: g.texture,
                x: g.x,
                y: g.y,
            }))

            this.createFragmentEffect(textures, groundLevel)

            // Remove all sprites
            set.forEach(g => {
                g.parent?.removeChild(g)
            })

            const compositeTileMeta = this.config.tilemapMetaProvider.resolveCompositeTileIdTileMeta(
                surfaceObject.compositeTileId,
                surfaceObject.tilesetId,
            )
            const breakageMeta = compositeTileMeta?.tileMeta?.breakageMeta
            if (breakageMeta) {
                breakageMeta.descriptors.forEach(descriptor => {
                    const key = `${descriptor.compositeTileId}-${x}-${y}`
                    this.surfaceObjectsByKey.delete(key)
                })
            }
        }
    }

    /**
     * Apply remove effect to a tile
     */
    public applyRemoveEffect = (effect: TileEffect): void => {
        const { tileId, surfaceId, mapRoughCoordinates } = effect
        const { x, y } = mapRoughCoordinates
        const key = surfaceId || `${tileId}-${x}-${y}`
        const set = this.surfaceObjectsByKey.get(key)?.spriteSet
        if (set) {
            set.forEach(g => {
                g.parent?.removeChild(g)
            })
        }
    }

    // ============================================================================
    // Fragment Effect Methods
    // ============================================================================

    /**
     * Create fragment effect for destroyed tiles
     */
    public createFragmentEffect = (
        textures: Array<{ texture: Texture; x: number; y: number }>,
        groundLevel: number,
    ): void => {
        const options: FragmentEffectOptions = {
            effectType: "crumble",
            fragmentCount: 8, // More fragments for multi-sprite tiles
            duration: 1000,
            animationSpeed: 16,
            gravity: 0.3,
            bounceFactor: 0.6,
            dustParticles: 8,
            groundLevel: groundLevel,
        }

        const effect = new FragmentEffect(this.config.surface)
        effect.createMultiTextureEffect(textures, options)
    }

    /**
     * Create single texture fragment effect
     */
    public createSingleFragmentEffect = (texture: Texture, parentX: number, parentY: number): void => {
        const options: FragmentEffectOptions = {
            effectType: "crumble",
            duration: 1000,
            animationSpeed: 16, // 60fps for smoother physics
            gravity: 0.3,
            bounceFactor: 0.6,
            dustParticles: 5,
            groundLevel: 20,
        }

        const effect = new FragmentEffect(this.config.surface)
        effect.createEffect(texture, parentX, parentY, options)
    }

    // ============================================================================
    // Damage Visualization Methods
    // ============================================================================

    /**
     * Apply damage visualization to existing tiles
     */
    public applyDamageVisualizationToExistingTiles = (surfaceObjectsByKey: Map<string, SurfaceObject>): void => {
        surfaceObjectsByKey.forEach((obj, key) => {
            const effect = this.damagedTiles.get(key)
            if (obj.delta?.context?.hp && obj.delta?.context?.hpMax) {
                const { hp, hpMax } = obj.delta.context
                this.applyDamageVisualization({ ...effect, effectType: "damage", hp, hpMax,
                    mapRoughCoordinates: { x: obj.delta.x, y: obj.delta.y } }, obj, obj.spriteSet,
                    obj.delta.x, obj.delta.y, false)
            } else if (effect) {
                const { x, y } = effect.mapRoughCoordinates
                this.applyDamageVisualization(effect, obj, obj.spriteSet, x, y, false)
            }
        })
    }

    // ============================================================================
    // Configuration Methods
    // ============================================================================

    /**
     * Update the tilemap meta provider
     */
    public updateTilemapMetaProvider = (tilemapMetaProvider: TileMapMetaProvider): void => {
        this.config.tilemapMetaProvider = tilemapMetaProvider
    }

    /**
     * Get the hole effect instance for advanced configuration
     */
    public getHoleEffect = (): HoleEffect => {
        return this.config.holeEffect
    }

    /** The map's surfaces move when its floor changes roles; new effects follow them. */
    public setSurfaces = (surface: Container, worldOverlaySurface: WorldOverlaySurface): void => {
        this.config.surface = surface
        this.config.worldOverlaySurface = worldOverlaySurface
        this.treeHit.setSurface(surface)
        this.treeFall.setSurface(surface)
    }

    /**
     * Update sky surfaces (called after SkyManager is initialized)
     */
    public updateSkySurfaces = (skySpriteSurfaceAbove: Container, skySpriteSurfaceBelow: Container): void => {
        this.config.skySpriteSurfaceAbove = skySpriteSurfaceAbove
        this.config.skySpriteSurfaceBelow = skySpriteSurfaceBelow
    }

    /**
     * Create damage overlay sprite for damaged tiles
     */
    public createDamageOverlaySprite = (tilesetTileId: string, x: number, y: number, surface?: Container): Sprite => {
        return this.config.attachTexture(contentTexture(tilesetTileId), surface)
    }

    /**
     * Check if a tile should have a damage overlay based on its delta
     */
    public shouldCreateDamageOverlay = (delta?: MapDelta): boolean => {
        return !!(delta && delta.context?.hp && delta.context.hp < delta.context.hpMax)
    }

    /**
     * Update hole effect context when room changes
     */
    public updateHoleEffectContext = (roomId: string, rng: () => number): void => {
        this.treeSway.reset()
        this.treeHit.clear()
        this.treeFall.clear()
        this.lastStrikeFrom.clear()
        this.predictedHits.clear()
        this.damagedTiles.clear()
        // Update hole effect with new room context
        this.config.holeEffect.updateRandomContext(roomId, rng)
    }

    /** Advance tree motion and expire unmatched local contacts. */
    public update = (wind: WindLeavesConfig | null = null, center?: Coordinate, storm = 0, brightness?: number): void => {
        this.treeSway.clear()
        this.treeHit.update()
        this.treeFall.update()
        this.treeSway.update(wind, center, storm, brightness, this.treeHit.isSwaying)
        this.predictedHits.forEach((hits, key) => {
            const live = hits.filter(hit => performance.now() - hit.at < 2000)
            if (live.length) this.predictedHits.set(key, live)
            else this.predictedHits.delete(key)
        })
    }

    private isTree = (obj: SurfaceObject, effect: TileEffect): boolean => {
        const tile = effect.entityTile || this.config.tilemapMetaProvider.resolveCompositeTileIdTileMeta(
            obj.compositeTileId, obj.tilesetId)?.tileMeta?.entityTileId
        return isTreeEntityTile(tile)
    }

    // A tree comes down rather than shattering: see TreeFall. It carries on from the swing the last blow
    // left it in, and goes over away from whoever felled it.
    private fellTree = (key: string, set: Sprite[], effect: TileEffect) => {
        const windLean = this.treeSway.release(key)
        const swing = this.treeHit.release(key)
        const lean = swing?.angle ?? windLean
        const from = effect.strikeFrom || this.lastStrikeFrom.get(key)
        this.lastStrikeFrom.delete(key)
        this.predictedHits.delete(key)
        const art = this.treeDamage.art(set)
        const left = Math.min(...set.map(s => s.x))
        const right = Math.max(...set.map(s => s.x + s.width))
        const trunkX = art ? left + (art.trunkLeft + art.trunkRight + 1) / 2 : (left + right) / 2
        const side = fallSide(trunkX, from, lean)
        this.treeFall.fell({ key, sprites: set, felled: this.treeDamage.felled(set, side), side, lean })
    }

    private strikeTree = (key: string, obj: SurfaceObject, effect: TileEffect): boolean => {
        const initialLean = this.treeSway.release(key)
        return this.treeHit.strike({
            initialLean, key, sprites: () => this.surfaceObjectsByKey.get(key)?.spriteSet,
            art: this.treeDamage.art(obj.spriteSet), from: effect.strikeFrom, strength: effect.strikeStrength ?? 1,
        })
    }

    public predictTreeStrike = (effect: TileEffect): void => {
        const { x, y } = effect.mapRoughCoordinates
        const key = effect.surfaceId || `${effect.tileId}-${x}-${y}`
        const obj = this.surfaceObjectsByKey.get(key)
        if (!obj || !this.isTree(obj, effect) || !this.strikeTree(key, obj, effect)) return
        if (effect.strikeFrom) this.lastStrikeFrom.set(key, effect.strikeFrom)
        const pending = this.predictedHits.get(key) || []
        pending.push({ strikerId: effect.strikerId, at: performance.now() })
        this.predictedHits.set(key, pending.slice(-8))
    }

    /** Release map transforms before a redraw recycles the tile sprites. */
    public clearSurfaceObjects = (): void => {
        this.treeSway.reset()
        this.treeHit.releaseSprites()
        this.surfaceObjectsByKey.clear()
    }

    // ============================================================================
    // Private Helper Methods (consolidated from TileDamageManager)
    // ============================================================================

    /**
     * Perform shake animation for damaged tiles
     */
    private performShakeAnimation = (set: Sprite[], surfaceObject: SurfaceObject, onComplete: () => void,
        isCurrent: () => boolean): void => {
        const startTs = Date.now()
        const duration = randomIntBetween(100, 200) // ms
        const frequency = randomIntBetween(20, 30) // Hz
        const initialAmplitude = randomIntBetween(3, 6) // pixels

        const original = set.map(g => ({ x: g.x, y: g.y }))

        // A sprite in `set` can be destroyed mid-shake by a concurrent incremental repaint (placing or
        // hitting an adjacent wall re-tiles this one -> removeSpritedCell destroys its owned sprites).
        // A destroyed sprite is still truthy but its transform is null, so `g.x = ...` throws
        // "Cannot read properties of null (reading 'position')". Skip any sprite that's gone.
        const alive = (g: Sprite): boolean => !!g && !g.destroyed && !!g.transform

        const interval = setInterval(() => {
            if (!isCurrent()) {
                clearInterval(interval)
                surfaceObject.isShaking = false
                return
            }
            const elapsed = Date.now() - startTs
            const progress = elapsed / duration

            if (progress >= 1.0) {
                clearInterval(interval)
                // Guaranteed restoration - ensure tiles return to exact original positions
                set.forEach((g, i) => {
                    if (!alive(g)) return
                    g.x = original[i].x
                })
                // Clear the shaking flag to allow future shake effects
                surfaceObject.isShaking = false
                onComplete()
                return
            }

            // Damped sine wave: amplitude * decay * sin(2π * frequency * time)
            const decay = Math.pow(1 - progress, 2) // Exponential decay
            const amplitude = initialAmplitude * decay
            const offset = amplitude * Math.sin(2 * Math.PI * frequency * (elapsed / 1000))

            set.forEach((g, i) => {
                if (!alive(g)) return
                g.x = original[i].x + offset
            })
        }, 16) // ~60fps
    }

    /**
     * Apply damage visualization after shake completes
     * Sprites are already in correct Y-order from processSurfaceId
     */
    private applyDamageVisualization = (
        effect: TileEffect,
        surfaceObject: SurfaceObject,
        set: Sprite[],
        x: number,
        y: number,
        showFeedback = true,
    ): void => {
        // This runs from the shake's onComplete, so `set` (captured before the shake) can now hold
        // sprites destroyed mid-shake by a concurrent full rerender / repaint. Reading their geometry
        // (breakage addChild, the HP-indicator's set[0].width) throws. Prune the shared spriteSet and
        // work from the live sprites; bail if nothing survives (the tile is gone -- nothing to visualize).
        if (surfaceObject.spriteSet) {
            surfaceObject.spriteSet = surfaceObject.spriteSet.filter(s => s && !s.destroyed && !!s.transform)
        }
        // Sprites are already in correct Y-order from processSurfaceId insertion
        const sorted = (surfaceObject.spriteSet || []).filter(s => s && !s.destroyed && !!s.transform)
        if (sorted.length === 0) {
            return
        }

        // The server only populates hpPct for tiles whose max hp > 10 (see entity_tile.ts);
        // low-hp tiles like wall-wood (hp 5) arrive with hpPct undefined, which would make
        // damagePct = 1 - undefined = NaN and draw no damage. Derive the fraction from the
        // hp/hpMax the effect always carries -- the same values the deferred (re-render) path
        // uses -- so immediate damage matches what a reload would show.
        const hpPct = effect.hpPct ?? (effect.hpMax ? effect.hp / effect.hpMax : undefined)

        const compositeTileMeta = this.config.tilemapMetaProvider.resolveCompositeTileIdTileMeta(
            surfaceObject.compositeTileId,
            surfaceObject.tilesetId,
        )
        if (this.isTree(surfaceObject, effect)) {
            const art = this.treeDamage.art(sorted)
            const left = Math.min(...sorted.map(s => s.x))
            const trunkX = left + (art ? (art.trunkLeft + art.trunkRight + 1) / 2 : sorted[0].texture.width / 2)
            const side = effect.strikeFrom && effect.strikeFrom.x < trunkX ? -1 : 1
            this.treeDamage.apply(sorted, 1 - (hpPct ?? 1), side)
            return
        }

        const breakageMeta = compositeTileMeta?.tileMeta?.breakageMeta

        if (breakageMeta) {
            const damagePct = 1.0 - hpPct
            if (!surfaceObject.flags) {
                surfaceObject.flags = new Set()
            }

            const breakSets: Sprite[][] = []
            breakageMeta?.descriptors.forEach((descriptor, i) => {
                const key = `${descriptor.compositeTileId}-${x}-${y}`
                const applies = between(damagePct, descriptor.thresholdLow, descriptor.thresholdHigh)
                if (applies) {
                    if (!surfaceObject.flags.has(key)) {
                        if (this.config.handleRenderCompositeTile) {
                            let breakset = this.config.handleRenderCompositeTile(
                                key,
                                this.config.tilemapMetaProvider,
                                descriptor.compositeTileId,
                                descriptor.tilesetId,
                            )
                            surfaceObject.flags.add(key)
                            breakSets.push(breakset)
                        }
                    }
                }
            })

            if (breakSets.length > 0) {
                breakSets.forEach(breakSet => {
                    sorted.forEach((next, i) => {
                        const renderable = breakSet[i]
                        // `next` can have been destroyed mid-shake by a concurrent repaint (same race as
                        // performShakeAnimation); addChild on a destroyed sprite throws. Skip if gone.
                        if (!renderable || !next || next.destroyed || !next.transform) return
                        // Position child sprites relative to parent, not absolute
                        renderable.x = 0
                        renderable.y = 0
                        next.addChild(renderable)
                    })
                })
            }
        } else {
            // Generate holes for more realistic damage
            this.config.holeEffect.generateDynamicHolesFromHp(surfaceObject, hpPct, x, y)
        }

        // Show HP percentage indicator
        if (showFeedback && effect.hpPct) {
            const indicator = new AnimatedFeedback()
            const location = this.config.worldOverlaySurface.computeLocation(x * TileSize, y * TileSize)

            indicator.setup(
                location.x + sorted[0].width / 2,
                location.y,
                undefined,
                `${Math.ceil(effect.hpPct * 100)}%`,
                this.config.worldOverlaySurface,
            )
        }
    }

    /**
     * Get cache statistics from the hole effect
     */
    public getCacheStats = (): { hits: number; misses: number; size: number; hitRate: number } => {
        return HoleEffect.getCacheStats()
    }

    /**
     * Clear the global damage cache
     */
    public clearGlobalCache = (): void => {
        HoleEffect.clearGlobalCache()
    }
}
